import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LocaleContext } from '../../components/LocaleContext';
import { Chart } from "primereact/chart";
import { Skeleton } from "primereact/skeleton";
import { Calendar } from "../../components/PrimeCalendar";
import { Dropdown } from "primereact/dropdown";
import { addLocale } from "primereact/api";
import { getRequest } from "../../api/ApiController";
import { DatabaseContext } from "../../redux/DatabaseStore";
import AuthContext from "../../components/AuthContext";
import UtilContext from "../../components/UtilContext";
import { hasCrmModuleCompleteList } from "../../utils/crmCommunicationPermissions";
import { formatCurrency } from "../../utils/currencyUtils";
import { getCurrency } from "../../utils/moduleSettings";
import { uiT } from '../../utils/uiTranslate';
import {
  buildSalesEvaluationCrmListDrilldownRequest,
  buildSalesEvaluationPeriodParams,
  buildTurnoverChartData,
  buildTurnoverDrilldownRequestFilters,
  colorNameToHex,
  normalizeTurnoverBySegment,
  readTotalTurnover,
  resolveTurnoverSegmentColor,
  toLocalDateString,
} from '../../utils/crmSalesTurnoverUi';
import { buildAcquisitionStatusClickFilters } from '../../utils/crmAcquisitionStatusContract';

const PAGE_KEY = "CustomerSalesEvaluation";

const PERIOD_OPTION_DEFS = [
  { labelKey: 'Common.Period.All', value: "Všetko" },
  { labelKey: 'Common.Period.Day', value: "Za deň" },
  { labelKey: 'Common.Period.Month', value: "Za mesiac" },
  { labelKey: 'Common.Period.Year', value: "Za rok" },
  { labelKey: 'Common.Period.Custom', value: "Vlastné" },
];

const PORTFOLIO_SEGMENTS = [
  { key: ["new", "new_client"], label: uiT('CustomerTypeOptions.NewClient'), settingKey: "portfolio_new_color", fallbackColor: "gray" },
  { key: ["potential", "potential_client"], label: uiT('CustomerTypeOptions.PotentialClient'), settingKey: "portfolio_potential_color", fallbackColor: "black" },
  { key: ["verified", "verified_client"], label: uiT('CustomerTypeOptions.VerifiedClient'), settingKey: "portfolio_verified_color", fallbackColor: "purple" },
  { key: ["inquiring", "inquiring_customer", "inquiring_client"], label: uiT('CrmSales.Type.Inquiring'), settingKey: "portfolio_inquiring_color", fallbackColor: "blue" },
  { key: ["occasional", "occasional_customer"], label: uiT('CrmSales.Type.Occasional'), settingKey: "portfolio_occasional_color", fallbackColor: "yellow" },
  { key: ["regular", "regular_customer"], label: uiT('CrmSales.Type.Regular'), settingKey: "portfolio_regular_color", fallbackColor: "orange" },
  { key: ["strategic", "strategic_customer"], label: uiT('CrmSales.Type.Strategic'), settingKey: "portfolio_strategic_color", fallbackColor: "green" },
  { key: ["lost", "lost_customer"], label: uiT('CrmSales.Type.Lost'), settingKey: "portfolio_lost_color", fallbackColor: "red" },
];

const ACQUISITION_SEGMENTS = [
  { key: ["unsuccessful", "failed"], label: uiT('CrmSales.Result.FailedF'), settingKey: "acquisition_unsuccessful_color", fallbackColor: "red" },
  { key: ["running", "in_progress"], label: uiT('CrmSales.Result.Running'), settingKey: "acquisition_running_color", fallbackColor: "orange" },
  { key: ["successful", "success"], label: uiT('CrmSales.Result.SuccessF'), settingKey: "acquisition_successful_color", fallbackColor: "green" },
];

const INQUIRY_EVAL_SEGMENTS = [
  { key: ["unsuccessful", "failed"], label: uiT('CrmSales.Result.FailedM'), settingKey: "inquiry_eval_unsuccessful_color", fallbackColor: "red" },
  { key: ["rejected"], label: uiT('CrmSales.Result.Rejected'), settingKey: "inquiry_eval_rejected_color", fallbackColor: "orange" },
  { key: ["successful", "success"], label: uiT('CrmSales.Result.SuccessM'), settingKey: "inquiry_eval_successful_color", fallbackColor: "green" },
];

const INQUIRY_STATE_PERIOD_SEGMENTS = [
  { key: ["rejections", "rejected"], label: "Zamietnutia", settingKey: "inquiry_state_rejections_color", fallbackColor: "gray" },
  { key: ["inquiries", "inquiry"], label: "Dopyty", settingKey: "inquiry_state_inquiries_color", fallbackColor: "blue" },
  { key: ["offers", "offer"], label: "Ponuky", settingKey: "inquiry_state_offers_color", fallbackColor: "green" },
];

const INQUIRY_STATE_ALL_SEGMENTS = [
  { key: ["inquiry", "inquiries"], label: "Dopyt", settingKey: "inquiry_state_inquiries_color", fallbackColor: "blue" },
  { key: ["offer", "offers"], label: "Ponuka", settingKey: "inquiry_state_offers_color", fallbackColor: "green" },
  { key: ["decision", "deciding"], label: "Rozhodovanie", settingKey: "inquiry_state_decision_color", fallbackColor: "orange" },
];

function getDatePickerProps(timePeriod) {
  if (timePeriod === "Za deň") return { dateFormat: "dd.mm.yy" };
  if (timePeriod === "Za mesiac") return { view: "month", dateFormat: "mm.yy" };
  if (timePeriod === "Za rok") return { view: "year", dateFormat: "yy" };
  if (timePeriod === "Vlastné") return { dateFormat: "dd.mm.yy", selectionMode: "range" };
  return { hidden: true };
}

function parseSavedPeriod(mask) {
  if (!mask || typeof mask !== "object") return null;
  const timePeriod = mask.timePeriod;
  const date = mask.date;
  if (!PERIOD_OPTION_DEFS.find((x) => x.value === timePeriod)) return null;
  if (timePeriod === "Všetko") return { timePeriod, date: null };
  if (timePeriod === "Vlastné" && Array.isArray(date) && date.length >= 2) {
    return { timePeriod, date: date.map((d) => (d ? new Date(d) : null)) };
  }
  if (date) return { timePeriod, date: new Date(date) };
  return { timePeriod, date: null };
}

function serializePeriod(timePeriod, date) {
  if (Array.isArray(date)) {
    return { timePeriod, date: date.map((x) => (x instanceof Date ? toLocalDateString(x) : null)) };
  }
  return { timePeriod, date: date instanceof Date ? toLocalDateString(date) : null };
}

function pickValue(section, aliases) {
  if (!section) return 0;
  if (Array.isArray(section)) return 0;
  for (const alias of aliases) {
    if (section[alias] != null) return Number(section[alias]) || 0;
  }
  return 0;
}

function mapChart(section, defs, getColor, t) {
  const labels = defs.map((d) => (d.labelKey && t ? t(d.labelKey) : d.label));
  const colors = defs.map((d) => getColor(d.settingKey, d.fallbackColor));
  const data = defs.map((d) => pickValue(section, d.key));
  return {
    labels,
    datasets: [{ data, backgroundColor: colors, hoverBackgroundColor: colors }],
  };
}

function emptyChart(defs, getColor, t) {
  return mapChart({}, defs, getColor, t);
}

function crmListTarget(page, mask, translationKey) {
  return {
    page,
    mask,
    translationKey,
    name: uiT(translationKey),
  };
}

const CRM_LIST_TARGETS = {
  business: {
    myCustomer: crmListTarget("CustomerMyBusinessByCustomerList", "cust025", "CrmSales.View.MyDealsByCustomer"),
    myState: crmListTarget("CustomerMyBusinessByStateList", "cust027", "CrmSales.View.MyDealsByState"),
    allCustomer: crmListTarget("CustomerAllBusinessByCustomerList", "cust028", "CrmSales.View.AllDealsByCustomer"),
    allState: crmListTarget("CustomerAllBusinessByStateList", "cust030", "CrmSales.View.AllDealsByState"),
  },
  acquisition: {
    myCustomer: crmListTarget("CustomerMyAcquisitionByCustomerList", "cust031", "CrmSales.View.MyAcqByCustomer"),
    myState: crmListTarget("CustomerMyAcquisitionByStateList", "cust033", "CrmSales.View.MyAcqByState"),
    allCustomer: crmListTarget("CustomerAllAcquisitionByCustomerList", "cust034", "CrmSales.View.AllAcqByCustomer"),
    allState: crmListTarget("CustomerAllAcquisitionByStateList", "cust036", "CrmSales.View.AllAcqByState"),
  },
};

/** Raw overall_status codes MUST match CrmAcquisitionStatusContract / BE widget buckets. */
const ACQUISITION_STATUS_CLICK_FILTERS = buildAcquisitionStatusClickFilters();

const INQUIRY_EVALUATION_CLICK_FILTERS = [
  { request: { filter_overall_status: "closed_failed" } },
  { request: { filter_overall_status: "closed_rejected" } },
  { request: { filter_overall_status: "closed_accepted" } },
];

const INQUIRY_STATE_PERIOD_CLICK_FILTERS = [
  { request: { filter_overall_status: "closed_rejected" } },
  { request: { filter_workflow_state_label: "Dopyt" } },
  { request: { filter_quote_offer_status: "Ponuka" }, header: [{ field: "quote_offer_status", value: "Ponuka" }] },
];

const INQUIRY_STATE_ALL_CLICK_FILTERS = [
  { request: { filter_workflow_state_label: "Dopyt" } },
  { request: { filter_workflow_state_label: "Ponuka" } },
  { request: { filter_workflow_state_label: "Rozhodovanie" } },
];

function segmentCodeFromDefinition(definition) {
  const keys = Array.isArray(definition?.key) ? definition.key : [];
  return keys.find((key) => String(key).includes("_")) || keys[0] || "";
}

export default function CustomerSalesEvaluation({ endpointPrefix = "/api/customer_sales_evaluation" }) {
  const { t, locale } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { setUserMaskFilter, toast } = useContext(UtilContext);
  const currencySymbol = getCurrency(state?.module_settings || []);
  const formatTurnover = useCallback(
    (value) => formatCurrency(Number(value) || 0, currencySymbol, 'sk'),
    [currencySymbol]
  );
  const loadedMaskRef = useRef(false);
  const loadSeqRef = useRef(0);
  const topCustomersListRef = useRef(null);
  const topCustomersHintRef = useRef(null);
  const topCustomersTopHintRef = useRef(null);
  const topCustomersCanScrollRef = useRef(false);
  const topCustomersScrollRafRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [traders, setTraders] = useState([]);
  const [canSelectTrader, setCanSelectTrader] = useState(null);
  const [selectedTraderId, setSelectedTraderId] = useState("");
  const [timePeriod, setTimePeriod] = useState("Všetko");
  const [date, setDate] = useState(null);
  const periodOptions = useMemo(
    () => PERIOD_OPTION_DEFS.map(({ labelKey, value }) => ({ label: t(labelKey), value })),
    [locale, t]
  );

  const getCrmColor = useCallback((key, fallback) => {
    const row = (state?.module_settings || []).find((s) => s.module === 'crm' && s.key === key);
    return colorNameToHex(row?.value || fallback);
  }, [state?.module_settings]);

  const resolveSegmentDotColor = useCallback(
    (segment) => resolveTurnoverSegmentColor(segment, getCrmColor),
    [getCrmColor]
  );

  const [portfolioData, setPortfolioData] = useState(() => emptyChart(PORTFOLIO_SEGMENTS, (k, f) => colorNameToHex(f)));
  const [turnoverData, setTurnoverData] = useState(() => ({
    labels: [],
    datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }],
    segments: [],
  }));
  const [totalTurnover, setTotalTurnover] = useState(0);
  const [acquisitionData, setAcquisitionData] = useState(() => emptyChart(ACQUISITION_SEGMENTS, (k, f) => colorNameToHex(f)));
  const [inquiryEvalData, setInquiryEvalData] = useState(() => emptyChart(INQUIRY_EVAL_SEGMENTS, (k, f) => colorNameToHex(f)));
  const [inquiryStateData, setInquiryStateData] = useState(() => emptyChart(INQUIRY_STATE_ALL_SEGMENTS, (k, f) => colorNameToHex(f), (k) => k));
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCustomersCanScroll, setTopCustomersCanScroll] = useState(false);

  const dateProps = useMemo(() => getDatePickerProps(timePeriod), [timePeriod]);
  const inquiryStateClickFilters = useMemo(
    () => (buildSalesEvaluationPeriodParams(timePeriod, date).time_period ? INQUIRY_STATE_PERIOD_CLICK_FILTERS : INQUIRY_STATE_ALL_CLICK_FILTERS),
    [date, timePeriod]
  );
  const traderOptions = useMemo(
    () => {
      const options = (traders || []).map((x) => ({ label: x.name, value: String(x.id) }));
      return canSelectTrader === true ? [{ label: t('Common.AllMale'), value: "" }, ...options] : options;
    },
    [canSelectTrader, traders, t]
  );
  const canOpenCompleteLists = hasCrmModuleCompleteList(user) || !!user?.is_superuser;

  const updateTopCustomersScrollState = useCallback(() => {
    const el = topCustomersListRef.current;
    const hint = topCustomersHintRef.current;
    const topHint = topCustomersTopHintRef.current;
    if (!el) {
      if (topCustomersCanScrollRef.current) {
        topCustomersCanScrollRef.current = false;
        setTopCustomersCanScroll(false);
      }
      if (hint) hint.style.opacity = '0';
      if (topHint) topHint.style.opacity = '0';
      return;
    }
    const canScroll = el.scrollHeight > el.clientHeight + 2;
    if (topCustomersCanScrollRef.current !== canScroll) {
      topCustomersCanScrollRef.current = canScroll;
      setTopCustomersCanScroll(canScroll);
    }
    if (hint) {
      const atBottom = !canScroll || el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      hint.style.opacity = atBottom ? '0' : '1';
    }
    if (topHint) {
      const atTop = !canScroll || el.scrollTop <= 4;
      topHint.style.opacity = atTop ? '0' : '1';
    }
  }, []);

  const handleTopCustomersScroll = useCallback(() => {
    if (topCustomersScrollRafRef.current) return;
    topCustomersScrollRafRef.current = requestAnimationFrame(() => {
      topCustomersScrollRafRef.current = null;
      updateTopCustomersScrollState();
    });
  }, [updateTopCustomersScrollState]);

  const openCrmEvaluationList = useCallback((kind, byState = false, filters = {}) => {
    const targetKey = `${canOpenCompleteLists ? "all" : "my"}${byState ? "State" : "Customer"}`;
    const target = CRM_LIST_TARGETS[kind]?.[targetKey];
    if (!target) return;

    const initialRequestFilters = buildSalesEvaluationCrmListDrilldownRequest({
      statusRequest: filters.request || {},
      timePeriod,
      date,
      selectedTraderId: selectedTraderId || undefined,
    });

    dispatch({
      type: "create_new_tab",
      name: target.name,
      translationKey: target.translationKey,
      legacyLabel: target.name,
      page: target.page,
      mask: target.mask,
      props: {
        initialRequestFilters,
        initialHeaderFilters: filters.header || [],
      },
    });
  }, [canOpenCompleteLists, date, dispatch, selectedTraderId, timePeriod]);

  /** Portfolio / non-turnover Central DB drilldown (activity scope — unchanged). */
  const openCentralDatabaseBySegment = useCallback((segmentCode) => {
    if (!segmentCode) return;
    const requestFilters = {
      sales_evaluation_scope: 1,
      filter_customer_type: segmentCode,
      ...buildSalesEvaluationPeriodParams(timePeriod, date),
    };
    if (selectedTraderId) requestFilters.selected_trader_id = selectedTraderId;
    dispatch({
      type: "create_new_tab",
      name: t('Menu.CRM.CentralDatabase'), translationKey: 'Menu.CRM.CentralDatabase', legacyLabel: t('Menu.CRM.CentralDatabase'),
      page: "CustomerCentralDatabaseList",
      mask: "cust011",
      props: {
        initialRequestFilters: requestFilters,
        initialHeaderFilters: [{ field: "customer_type", value: segmentCode }],
      },
    });
  }, [date, dispatch, selectedTraderId, t, timePeriod]);

  /** CUST005 turnover drilldown — BE #107 sales_evaluation_turnover_segment only. */
  const openTurnoverDrilldownBySegment = useCallback((segmentCode) => {
    if (!segmentCode) return;
    const requestFilters = buildTurnoverDrilldownRequestFilters({
      segmentCode,
      timePeriod,
      date,
      selectedTraderId: selectedTraderId || undefined,
    });
    dispatch({
      type: "create_new_tab",
      name: t('Menu.CRM.CentralDatabase'), translationKey: 'Menu.CRM.CentralDatabase', legacyLabel: t('Menu.CRM.CentralDatabase'),
      page: "CustomerCentralDatabaseList",
      mask: "cust011",
      props: {
        initialRequestFilters: requestFilters,
        initialHeaderFilters: [],
      },
    });
  }, [date, dispatch, selectedTraderId, t, timePeriod]);

  const loadDashboardData = useCallback(async () => {
    const loadSeq = loadSeqRef.current + 1;
    loadSeqRef.current = loadSeq;
    setLoading(true);
    try {
      const params = buildSalesEvaluationPeriodParams(timePeriod, date);
      if (selectedTraderId) params.selected_trader_id = selectedTraderId;
      const query = new URLSearchParams(params).toString();
      const url = query ? `${endpointPrefix}?${query}` : endpointPrefix;
      const resp = await getRequest(url);
      if (loadSeqRef.current !== loadSeq) return;
      const data = resp?.data;
      const scope = data?.scope || {};
      const widgets = data?.widgets || {};
      const periodScoped = Boolean(params.time_period);

      setTraders(Array.isArray(data?.traders) ? data.traders : []);
      setCanSelectTrader(Boolean(scope?.can_select_trader));
      setPortfolioData(mapChart(widgets?.portfolio, PORTFOLIO_SEGMENTS, getCrmColor));

      const segments = normalizeTurnoverBySegment(widgets);
      setTurnoverData(buildTurnoverChartData(segments, t, getCrmColor));
      setTotalTurnover(readTotalTurnover(widgets));

      setAcquisitionData(mapChart(widgets?.acquisitionStatus || widgets?.acquisitions, ACQUISITION_SEGMENTS, getCrmColor));
      setInquiryEvalData(mapChart(widgets?.inquiryEvaluation || widgets?.inquiriesEvaluation, INQUIRY_EVAL_SEGMENTS, getCrmColor));
      setInquiryStateData(
        mapChart(
          widgets?.inquiryState || widgets?.inquiriesState,
          periodScoped ? INQUIRY_STATE_PERIOD_SEGMENTS : INQUIRY_STATE_ALL_SEGMENTS,
          getCrmColor,
          t
        )
      );
      const top = widgets?.topCustomers || widgets?.top_customers || [];
      const normalizedTop = Array.isArray(top)
        ? top.map((x) => ({
            id: x.id ?? `${x.customer_name || x.name}-${x.turnover || 0}`,
            name: x.customer_name || x.name || "—",
            turnover: Number(x.turnover || x.amount || 0),
            segment: x.segment || x.customer_type || "",
          }))
        : [];
      setTopCustomers(normalizedTop);
    } catch (error) {
      if (loadSeqRef.current !== loadSeq) return;
      toast?.current?.show({
        severity: "error",
        summary: t('Common.LoadFailed'),
        detail: error?.message || t('CrmSales.LoadFailedDetail'),
        life: 3500,
      });
    } finally {
      if (loadSeqRef.current === loadSeq) setLoading(false);
    }
  }, [timePeriod, date, selectedTraderId, endpointPrefix, getCrmColor, toast, t]);

  useEffect(() => {
    addLocale("sk", {
      firstDayOfWeek: 1,
      showMonthAfterYear: false,
      dayNames: ["Nedela", "Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota"],
      dayNamesShort: ["Ne", "Pon", "Ut", "Str", "Štv", "Pia", "So"],
      dayNamesMin: ["Ne", "Po", "Ut", "Str", "Štv", "Pi", "So"],
      monthNames: ["Január", "Február", "Marec", "Apríl", "Máj ", "Jún", "Júl", "August", "September", "Október", "November", "December"],
      monthNamesShort: ["Jan", "Feb", "Mar", "Apr", "Máj ", "Jún", "Júl", "Aug", "Sep", "Okt", "Nov", "Dec"],
      today: t('Common.Today'),
      clear: t('Common.Delete'),
    });
  }, [t]);

  useEffect(() => {
    if (loadedMaskRef.current) return;
    const row = (state?.user_mask_filters || []).find((m) => m.page === PAGE_KEY);
    if (!row?.attributes) return;
    loadedMaskRef.current = true;
    try {
      const attrs = typeof row.attributes === "string" ? JSON.parse(row.attributes) : row.attributes;
      if (attrs?.selected_trader_id != null) setSelectedTraderId(String(attrs.selected_trader_id));
      const parsedPeriod = parseSavedPeriod(attrs?.period);
      if (parsedPeriod) {
        setTimePeriod(parsedPeriod.timePeriod);
        setDate(parsedPeriod.date);
      }
    } catch (_) {
      // ignore malformed mask
    }
  }, [state?.user_mask_filters]);

  useEffect(() => {
    if (canSelectTrader !== false) return;
    if (!user?.id) return;
    setSelectedTraderId(String(user.id));
  }, [canSelectTrader, user?.id]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    requestAnimationFrame(updateTopCustomersScrollState);
  }, [loading, topCustomers, topCustomersCanScroll, updateTopCustomersScrollState]);

  useEffect(() => () => {
    if (topCustomersScrollRafRef.current) {
      cancelAnimationFrame(topCustomersScrollRafRef.current);
    }
  }, []);

  const saveMask = useCallback(() => {
    const payload = {
      selected_trader_id: selectedTraderId || null,
      period: serializePeriod(timePeriod, date),
    };
    setUserMaskFilter(PAGE_KEY, "set_attributes", null, null, payload);
    toast?.current?.show({
      severity: "success",
      summary: t('Common.Mask'),
      detail: t('Common.Toast.MaskSavedDetail'),
      life: 2200,
    });
  }, [date, selectedTraderId, setUserMaskFilter, timePeriod, toast, t]);

  const chartOptions = useMemo(
    () => ({
      plugins: { legend: { display: false } },
      maintainAspectRatio: false,
    }),
    []
  );

  const turnoverChartOptions = useMemo(
    () => ({
      ...chartOptions,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(context) {
              const idx = context.dataIndex;
              const seg = turnoverData?.segments?.[idx];
              const amount = Number(seg?.amount ?? context.raw ?? 0) || 0;
              const count = Number(seg?.customer_count ?? 0) || 0;
              return [
                `${t('CrmSales.Tooltip.Turnover')}: ${formatTurnover(amount)}`,
                `${t('CrmSales.Tooltip.Customers')}: ${count}`,
              ];
            },
          },
        },
      },
    }),
    [chartOptions, formatTurnover, t, turnoverData?.segments]
  );

  const plugin = useMemo(
    () => ({
      id: "emptyDoughnut",
      afterDraw(chart) {
        const values = chart?.data?.datasets?.[0]?.data || [];
        const count = values.reduce((acc, x) => acc + (Number(x) || 0), 0);
        if (count > 0) return;
        const ctx = chart.ctx;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "20px Arial";
        ctx.fillStyle = "#9ca3af";
        ctx.fillText(t('ServiceTaskPreview.NoRecords'), chart.width / 2, chart.height / 2);
      },
    }),
    [t]
  );

  const renderLegend = (labels, colors, segments) => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
      {labels.map((label, i) => {
        const seg = segments?.[i];
        const count = seg?.customer_count;
        return (
          <span key={`${label}-${i}`} className="inline-flex items-center gap-1.5" title={seg
            ? `${label} — ${t('CrmSales.Tooltip.Turnover')}: ${formatTurnover(seg.amount)}; ${t('CrmSales.Tooltip.Customers')}: ${count ?? 0}`
            : label}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colors?.[i] || "#6b7280" }} />
            {label}
            {count != null ? (
              <span className="text-xs text-gray-500">({count})</span>
            ) : null}
          </span>
        );
      })}
    </div>
  );

  const ChartCard = ({
    title,
    chartData,
    onSegmentClick,
    totalLabel = t('CrmSales.TotalCount'),
    totalFormatter = (value) => value,
    totalOverride = null,
    chartOptionsOverride = null,
    showSegmentCounts = false,
  }) => (
    <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4 min-w-0 h-full flex flex-col">
      <h2 className="text-center text-xl font-semibold mb-4">{title}</h2>
      <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
        {loading ? (
          <Skeleton shape="circle" size="320px" className="flex-shrink-0" />
        ) : (
          <Chart
            type="doughnut"
            data={chartData}
            options={{
              ...(chartOptionsOverride || chartOptions),
              onClick: (_evt, items) => {
                if (!items?.length || !onSegmentClick) return;
                onSegmentClick(items[0].index);
              },
            }}
            className={`w-full h-full ${onSegmentClick ? "cursor-pointer" : ""}`}
            plugins={[plugin]}
          />
        )}
      </div>
      {!loading && renderLegend(
        chartData.labels,
        chartData.datasets?.[0]?.backgroundColor,
        showSegmentCounts ? chartData.segments : null
      )}
      <div className="text-center font-semibold mt-4">
        {loading ? (
          <Skeleton width="8rem" height="1.5rem" className="mx-auto" />
        ) : (
          `${totalLabel}: ${totalFormatter(
            totalOverride != null
              ? totalOverride
              : (chartData.datasets?.[0]?.data || []).reduce((a, b) => a + (Number(b) || 0), 0)
          )}`
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <div className="lg:grid grid-cols-3 gap-1 px-4 pt-4">
          <div />
          <div>
            <div className="flex items-center gap-3 mt-2">
              <label className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.TraderLabel')}</label>
              <Dropdown
                className="w-full crm-select-bordered"
                value={selectedTraderId}
                options={traderOptions}
                onChange={(e) => setSelectedTraderId(e.value || "")}
                placeholder={t('Common.SelectTrader')}
                disabled={canSelectTrader !== true}
              />
            </div>
          <div className="flex items-center gap-3 mt-2">
            <label className="text-xl font-semibold text-gray-800 shrink-0">{t('PreviewChecklists.PeriodLabel')}</label>
            <Dropdown
              className="w-full crm-select-bordered"
              value={timePeriod}
              options={periodOptions}
              onChange={(e) => setTimePeriod(e.value)}
            />
            <button
              type="button"
              onClick={loadDashboardData}
              title={t('Common.RefreshData')}
              className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 text-white transition-colors"
            >
              <i className="pi pi-refresh" />
            </button>
            <button
              type="button"
              onClick={saveMask}
              title={t('ServiceTaskPreview.SavePeriodHint')}
              className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
            >
              {t('Common.SaveMask')}
            </button>
          </div>
            <div className={dateProps.hidden ? "hidden" : "mt-2 min-w-0"}>
              <Calendar
                value={date}
                onChange={(e) => setDate(e.value)}
                locale="sk"
                showIcon
                className="w-full crm-calendar-bordered"
                hideOnRangeSelection
                {...dateProps}
              />
            </div>
          </div>
          <div />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 items-stretch">
          <ChartCard
            title={t('CrmSales.CustomerPortfolio')}
            chartData={portfolioData}
            onSegmentClick={(index) => openCentralDatabaseBySegment(segmentCodeFromDefinition(PORTFOLIO_SEGMENTS[index]))}
          />
          <ChartCard
            title={t('CrmSales.CustomerTurnovers')}
            chartData={turnoverData}
            onSegmentClick={(index) => {
              const code = turnoverData?.segments?.[index]?.code;
              if (code) openTurnoverDrilldownBySegment(code);
            }}
            totalLabel={t('CrmSales.TotalTurnover')}
            totalFormatter={formatTurnover}
            totalOverride={totalTurnover}
            chartOptionsOverride={turnoverChartOptions}
            showSegmentCounts
          />

          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4 min-w-0 h-full min-h-0 overflow-hidden flex flex-col">
            <h2 className="text-xl font-semibold mb-1">{t('CrmSales.TopCustomersByTurnover')}</h2>
            <div className="text-sm text-gray-600 mb-3">
              {t('CrmSales.TotalTurnover')}: {loading ? '…' : formatTurnover(totalTurnover)}
            </div>
            {loading ? (
              <div className="relative min-h-0 flex-1 overflow-hidden">
                <div className="absolute inset-0 space-y-2 overflow-hidden pr-1">
                  <Skeleton width="100%" height="2rem" />
                  <Skeleton width="100%" height="2rem" />
                  <Skeleton width="100%" height="2rem" />
                  <Skeleton width="100%" height="2rem" />
                  <Skeleton width="100%" height="2rem" />
                </div>
              </div>
            ) : (
              <div className="relative flex-1 min-h-0 overflow-hidden">
                <ul
                  ref={topCustomersListRef}
                  onScroll={handleTopCustomersScroll}
                  className="absolute inset-0 list-none m-0 p-0 space-y-2 overflow-y-auto no-scrollbar pr-2 pb-8"
                >
                  {topCustomers.length === 0 ? (
                    <li className="text-sm text-gray-500">{t('ServiceTaskPreview.NoRecords')}</li>
                  ) : (
                    topCustomers.map((item) => (
                      <li key={item.id} className="flex items-center gap-2 min-w-0">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: resolveSegmentDotColor(item.segment) }} />
                        <span className="truncate min-w-0 font-medium" title={item.name}>{item.name}</span>
                        <span className="ml-auto shrink-0 text-gray-700">
                          {formatTurnover(item.turnover)}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
                {topCustomersCanScroll ? (
                  <div ref={topCustomersTopHintRef} className="pointer-events-none absolute inset-x-0 top-0 flex justify-center rounded-t-xl bg-gradient-to-b from-gray-100 via-gray-100/90 to-transparent pb-8 pt-1 opacity-0 transition-opacity duration-200">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-medium text-gray-500 shadow-sm ring-1 ring-gray-200">
                      <i className="pi pi-angle-up text-xs" />
                      {t('CrmSales.Scroll.Previous')}
                    </span>
                  </div>
                ) : null}
                {topCustomersCanScroll ? (
                  <div ref={topCustomersHintRef} className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center rounded-b-xl bg-gradient-to-t from-gray-200 via-gray-200/90 to-transparent pb-1 pt-8 opacity-0 transition-opacity duration-200">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-medium text-gray-500 shadow-sm ring-1 ring-gray-200">
                      {t('CrmSales.Scroll.More')}
                      <i className="pi pi-angle-down text-xs" />
                    </span>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <ChartCard
            title={t('CrmSales.AcquisitionStatus')}
            chartData={acquisitionData}
            onSegmentClick={(index) => openCrmEvaluationList("acquisition", true, ACQUISITION_STATUS_CLICK_FILTERS[index])}
          />
          <ChartCard
            title={t('CrmSales.InquiryEvaluation')}
            chartData={inquiryEvalData}
            onSegmentClick={(index) => openCrmEvaluationList("business", true, INQUIRY_EVALUATION_CLICK_FILTERS[index])}
          />
          <ChartCard
            title={t('CrmSales.InquiryStatus')}
            chartData={inquiryStateData}
            onSegmentClick={(index) => openCrmEvaluationList("business", true, inquiryStateClickFilters[index])}
          />
        </div>
      </div>
    </div>
  );
}
